/**
 * RunDetailDrawer: one run in full. Runner, ministry, channel, version, timings, every step's status,
 * time, ticked sub-checks and note, how a reported problem was resolved, and the AI chat transcript.
 */

import { useQuery } from '@tanstack/react-query';
import { IconUser, IconBuildingChurch, IconDeviceMobile, IconVersions, IconClock, IconFlag, IconMessageCircle, IconCheck, IconRobot, IconNote, IconAlertCircle } from '@tabler/icons-react';
import { Drawer, Stack, Group, Text, Badge, Paper, Skeleton, Alert, SimpleGrid, ThemeIcon, Divider } from '@/components/ui';
import { useProceduresServices, type ProcedureChatMessageView } from '@actschurch/ccms-sdk';
import { DetailRow } from '../../FormsManagement/components/FormsUi';
import { RunStatusBadge, RunStepStatusBadge } from './ProceduresBadges';
import { proceduresKeys, proceduresErrorMessage, formatStampDateTime, formatStampTime, formatDuration, PROCEDURES_RUN_STEP_STATUS } from './proceduresUtils';

const badgeStyles = { root: { textTransform: 'none' as const } };

interface RunDetailDrawerProps {
  runId: number | null;
  onClose: () => void;
}

export function ChatMessageRow({ message }: { message: ProcedureChatMessageView }) {
  const isUser = message.role === 'user' || message.roleID === 1;
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <ThemeIcon size={28} radius="xl" variant="light" color={isUser ? 'blue' : 'violet'} style={{ flexShrink: 0 }}>
        {isUser ? <IconUser size={15} /> : <IconRobot size={15} />}
      </ThemeIcon>
      <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
        <Group gap={6} wrap="wrap">
          <Text size="xs" fw={600}>
            {isUser ? message.askedBy || 'Question' : 'AI assistant'}
          </Text>
          <Text size="xs" c="dimmed">
            {formatStampDateTime(message.createdDateTime)}
          </Text>
          {!isUser && message.answered === false && (
            <Badge size="xs" color="orange" variant="light" styles={badgeStyles}>
              Not covered by the material
            </Badge>
          )}
        </Group>
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {message.text}
        </Text>
        {message.citations.length > 0 && (
          <Group gap={4} wrap="wrap">
            {message.citations.map((c, i) => (
              <Badge key={i} size="xs" variant="outline" color="gray" styles={badgeStyles}>
                {c.kind === 'step' ? `Step ${c.stepNumber ?? ''}`.trim() : c.documentName || 'Document'}
              </Badge>
            ))}
          </Group>
        )}
      </Stack>
    </Group>
  );
}

export function RunDetailDrawer({ runId, onClose }: RunDetailDrawerProps) {
  const { proceduresAdmin } = useProceduresServices();
  const { data, isLoading, error } = useQuery({
    queryKey: proceduresKeys.run(runId ?? 0),
    queryFn: () => proceduresAdmin.getRun(runId as number),
    enabled: runId != null,
  });
  const run = data?.run;

  return (
    <Drawer opened={runId != null} onClose={onClose} position="right" size="lg" title={<Text fw={600}>{run ? `Run of ${run.procedureTitle}` : 'Run'}</Text>}>
      {isLoading ? (
        <Stack gap="sm">
          <Skeleton height={90} radius="md" />
          <Skeleton height={240} radius="md" />
        </Stack>
      ) : error ? (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {proceduresErrorMessage(error, "Couldn't load the run.")}
        </Alert>
      ) : run ? (
        <Stack gap="lg" data-help-id="procedures.runs.detail">
          <Paper withBorder radius="md" p="md">
            <Stack gap="sm">
              <Group justify="space-between" wrap="wrap">
                <Text fw={700}>{run.runnerName || 'Unknown runner'}</Text>
                <RunStatusBadge statusId={run.statusID} name={run.statusName} />
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <DetailRow icon={<IconBuildingChurch size={16} />} label="Ministry">
                  <Text size="sm">{run.ministryName || data?.ministryOther || 'Not given'}</Text>
                </DetailRow>
                <DetailRow icon={<IconDeviceMobile size={16} />} label="How they ran it">
                  <Text size="sm">{run.channelName}</Text>
                </DetailRow>
                <DetailRow icon={<IconVersions size={16} />} label="Version">
                  <Text size="sm">Version {run.versionNumber}</Text>
                </DetailRow>
                <DetailRow icon={<IconCheck size={16} />} label="Steps done">
                  <Text size="sm">
                    {run.stepsDone} of {run.stepsTotal}
                  </Text>
                </DetailRow>
                <DetailRow icon={<IconClock size={16} />} label="Started">
                  <Text size="sm">{formatStampDateTime(run.startedAt)}</Text>
                </DetailRow>
                <DetailRow icon={<IconClock size={16} />} label="Finished">
                  <Text size="sm">
                    {run.completedAt ? formatStampDateTime(run.completedAt) : 'Not finished'}
                    {data?.durationSeconds != null ? ` (${formatDuration(data.durationSeconds)})` : ''}
                  </Text>
                </DetailRow>
              </SimpleGrid>
              {run.note && (
                <DetailRow icon={<IconNote size={16} />} label="Note at the end">
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {run.note}
                  </Text>
                </DetailRow>
              )}
            </Stack>
          </Paper>

          <Stack gap="xs">
            <Text fw={600}>Steps</Text>
            <Stack gap={6}>
              {run.steps.map((s) => {
                const problem = s.statusID === PROCEDURES_RUN_STEP_STATUS.Problem;
                return (
                  <Paper key={s.stepID} withBorder radius="md" p="xs" style={{ borderLeft: `4px solid var(--mantine-color-${problem ? 'red' : s.statusID ? 'green' : 'gray'}-6)` }}>
                    <Group gap="sm" wrap="nowrap" align="flex-start">
                      <ThemeIcon size={24} radius="xl" variant="light" color={problem ? 'red' : s.statusID ? 'green' : 'gray'} style={{ flexShrink: 0 }}>
                        {problem ? <IconFlag size={13} /> : <Text size="xs" fw={700}>{s.stepNumber}</Text>}
                      </ThemeIcon>
                      <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
                        <Group gap="xs" wrap="wrap">
                          <Text size="sm" fw={600}>
                            {s.title}
                          </Text>
                          <RunStepStatusBadge statusId={s.statusID} size="xs" />
                          {s.checkedAt && (
                            <Text size="xs" c="dimmed">
                              {formatStampTime(s.checkedAt)}
                            </Text>
                          )}
                        </Group>
                        {s.checkedIds.length > 0 && (
                          <Text size="xs" c="dimmed">
                            {s.checkedIds.length} sub-check{s.checkedIds.length === 1 ? '' : 's'} ticked
                          </Text>
                        )}
                        {s.note && (
                          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                            {s.note}
                          </Text>
                        )}
                        {problem && (
                          <Group gap="xs">
                            <Badge size="sm" variant="light" color={s.problemResolved ? 'green' : 'red'} styles={badgeStyles}>
                              {s.problemResolved ? 'Problem resolved' : 'Problem open'}
                            </Badge>
                          </Group>
                        )}
                        {s.problemResolutionNote && (
                          <Text size="xs" c="dimmed">
                            Resolution: {s.problemResolutionNote}
                          </Text>
                        )}
                      </Stack>
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
          </Stack>

          <Divider />
          <Stack gap="sm">
            <Group gap="xs">
              <IconMessageCircle size={18} />
              <Text fw={600}>AI chat during this run</Text>
            </Group>
            {(data?.chat ?? []).length === 0 ? (
              <Text size="sm" c="dimmed">
                No questions were asked during this run.
              </Text>
            ) : (
              (data?.chat ?? []).map((m) => <ChatMessageRow key={m.messageID} message={m} />)
            )}
          </Stack>
        </Stack>
      ) : null}
    </Drawer>
  );
}
